import { definePlugin } from '../../framework';
import type { FieldSuggestion, TransformState, TransformStep } from '../../types';
import {
  acceptedValues,
  confirmingTextsFor,
  fieldExpectation,
  type FieldExpectation,
} from '../../utils/confirmation';
import { unwrapSnippet } from '../../utils/snippets';

const MIN_LENGTH = 2;

// The characters Split Handling splits on. A text bounded by these (or the
// ends of the value) is a part Split or Remove Words already offers.
const isBoundary = (char: string | undefined) => char === undefined || /[\s/,.;|-]/.test(char);

/**
 * The editor's Substring block: from an index to the end, or between two
 * indices (end not included).
 */
export const buildSubstringSnippet = (snippet: unknown, from: number, to?: number) => ({
  $declareVariable: { varToParse: unwrapSnippet(snippet) },
  '$varToParse.substring': to === undefined ? from : [from, to],
});

/**
 * Where a confirmed text sits inside a value, when no separator marks it off
 * ("V#0031W" → "0031W"). Parts between separators are left to Split
 * Handling and Remove Words. Null when the text isn't there exactly once.
 */
export const findSubstring = (
  value: string,
  text: string,
): { from: number; to?: number } | null => {
  const from = value.indexOf(text);
  if (from < 0 || value.indexOf(text, from + 1) >= 0) return null;
  const end = from + text.length;
  if (isBoundary(value[from - 1]) && isBoundary(value[end])) return null;
  // A text at the end runs to the end, so it doesn't depend on its length.
  return end === value.length ? { from } : { from, to: end };
};

/** Parts of a word in the value that something confirms. */
export const substringCandidates = (
  state: TransformState,
  suggestion: FieldSuggestion,
  others: FieldSuggestion[],
  field: FieldExpectation,
  enumValues: string[] = [],
): TransformState[] => {
  return confirmingTextsFor(state, suggestion, others, field, enumValues).flatMap((text) => {
    if (text.length < MIN_LENGTH) return [];
    const position = findSubstring(state.value as string, text);
    if (position === null) return [];
    return [{ snippet: buildSubstringSnippet(state.snippet, position.from, position.to), value: text }];
  });
};

export const substringStep: TransformStep = {
  name: 'substring',
  stage: 'split',
  mode: 'add',
  apply: (state, { entry, suggestion, suggestions }) =>
    substringCandidates(state, suggestion, suggestions, fieldExpectation(entry), acceptedValues(entry)),
};

export const substring = definePlugin<{}>({
  name: 'Substring',
  version: '0.1.0',
  description:
    'Replaces the Substring transformer: takes part of a word when something confirms it.',
  initialState: {},

  bootstrap: () => {},

  transformSteps: () => [substringStep],
});
